import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import VolumeCalculator from "../VolumeCalculator";
import { VOLUME_TIERS } from "../../data/fees";
import { useSiteT } from "../../hooks/useSiteT";

export const tierLabel = (t, label) => (label.startsWith("Under") ? t("tier.under") : label.replace(" / mo", t("tier.mo")));

function Rate({ value }) {
    if (value === 0) return <span className="font-display text-2xl md:text-3xl text-highlight">0%</span>;
    return <span className="font-display text-2xl md:text-3xl text-foreground">{value}%</span>;
}

export default function VolumeTiersSection() {
    const { t } = useSiteT();
    const COLS = [
        { key: "whiteHat", label: "White-hat" },
        { key: "grayHat", label: "Gray-hat" },
        { key: "casino", label: "Casino" },
    ];
    return (
        <section id="volume-rewards" data-testid="volume-rewards-section" className="py-24 md:py-32 relative border-t border-white/[0.06]">
            <div className="container-skala">
                <div className="max-w-3xl mb-16">
                    <div className="inline-flex items-center gap-2 mb-5">
                        <span className="h-px w-8 bg-highlight" />
                        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-highlight">{t("vt.eyebrow")}</p>
                    </div>
                    <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tight leading-[1.03]">
                        {t("vt.h1")} <span className="serif-accent text-foreground/85">{t("vt.h2")}</span>
                    </h2>
                    <p className="text-muted-foreground text-base md:text-lg mt-5 max-w-2xl leading-relaxed">
                        {t("vt.sub1")} <span className="text-foreground font-medium">{t("vt.sub2")}</span>{t("vt.sub3")} <span className="text-highlight font-medium">up to 3% cashback</span> {t("vt.sub4")}
                    </p>
                </div>

                {/* Editorial tier ladder — stacked rows, hairline dividers */}
                <div data-testid="tier-ladder" className="border-t border-white/[0.1]">
                    <div className="hidden md:grid grid-cols-[1.5fr_1fr_1fr_1fr] py-4 border-b border-white/[0.06]">
                        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">{t("vt.tierCol")}</span>
                        {COLS.map((c) => (
                            <span key={c.key} className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground text-right md:text-left">{c.label}</span>
                        ))}
                    </div>

                    {VOLUME_TIERS.map((tier) => (
                        <div key={tier.id} data-testid={`tier-row-${tier.id}`} className="group grid grid-cols-2 md:grid-cols-[1.5fr_1fr_1fr_1fr] gap-y-4 items-center py-7 border-b border-white/[0.06] transition-colors hover:bg-white/[0.015]">
                            <div className="col-span-2 md:col-span-1">
                                <div className="flex items-center gap-3">
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: tier.accent }} />
                                    <h3 className="font-display text-2xl md:text-3xl tracking-tight">{tier.name}</h3>
                                    {tier.cashback && (
                                        <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-highlight border border-highlight/30 rounded-full px-2 py-0.5">+{tier.cashback.min}–{tier.cashback.max}% cashback</span>
                                    )}
                                </div>
                                <p className="text-xs font-mono text-muted-foreground tracking-wide mt-1.5 ml-4">{tierLabel(t, tier.label)}</p>
                            </div>
                            {COLS.map((c) => (
                                <div key={c.key} className="flex flex-col">
                                    <span className="md:hidden text-[9px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">{c.label}</span>
                                    <Rate value={tier[c.key]} />
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                <p className="text-sm text-muted-foreground mt-6 max-w-2xl">{t("vt.c1")}</p>

                <div className="mt-14">
                    <VolumeCalculator />
                </div>

                <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                    <Link to="/signup" data-testid="volume-cta-apply">
                        <Button size="lg" className="btn-highlight h-12 px-8 rounded-md">{t("vt.cta")}</Button>
                    </Link>
                    <p className="text-xs text-muted-foreground font-mono">{t("vt.note")}</p>
                </div>
            </div>
        </section>
    );
}
